import React from 'react';
import { useLocation } from 'react-router-dom';
import { ROUTE_PATHS } from '@constants';
import { ACCOUNT_USER_ROLE, PLATFORM_ID, PLATFORM_NAME, PURCHASE_EVENT_STATUS, SCRIPT_TAG_STATUS } from '@enums';
import { useStore } from '@hooks';
import { Space } from 'antd';
import { observer } from 'mobx-react';
import Notification from './components/Notification';
import { ACCOUNT_BLOCKED_REASONS, createNotificationsMap, NOTIFICATION_TYPES } from './constants';
import { StyledNotificationsWrapper } from './styled';

const {
    ACCOUNT_ON_PAUSE,
    ACCOUNT_UNINSTALLED,
    PAYMENT_CONNECT,
    NOTIFICATION_UPDATE,
    MANUAL_STORE_ONBOARDING,
    SCRIPT_TAG_THANK_YOU_PAGE_CONNECT,
    SCRIPT_TAG_CONNECT
} = NOTIFICATION_TYPES;

const { CURRENCY, BAD_PLAN, INTEGRATION_INACTIVE, MISSING_US_SHIPPING_METHOD, SETUP_REQUIRED } =
    ACCOUNT_BLOCKED_REASONS;

const settings = {
    [PLATFORM_ID.SHOPIFY]: {
        currency: '/settings/general',
        badPlan: '/settings/plan',
        missedShipping: '/settings/shipping',
        integrationInactive: '/settings/app',
        setupRequired: '/settings'
    },
    [PLATFORM_ID.BIGCOMMERCE]: {
        currency: '/manage/settings/currencies',
        badPlan: 'https://manage.bigcommerce.com/account/stores/upgrade',
        missedShipping: '/manage/settings/shipping',
        integrationInactive: '/manage/marketplace/apps/my-apps'
    }
};

const NotificationsList = ({ notifications }) => {
    return (
        <Space data-e2e='notification_section' direction='vertical' size='middle' style={{ display: 'flex' }}>
            {notifications.map((notification) => (
                <Notification notification={notification} key={notification.title} />
            ))}
        </Space>
    );
};

const TopNotificationsSection = observer(() => {
    const store = useStore();
    const { me } = store.authStore;
    const isDeveloper = me?.permission?.role === ACCOUNT_USER_ROLE.DEVELOPER;

    if (isDeveloper) {
        return null;
    }

    const {
        setupInfo,
        isPaused,
        isUninstalled,
        isBlocked,
        isShopify,
        isManual,
        showPaymentFailedModal,
        companyName,
        currentAccount,
        shopifySettingsLink,
        bigcommerceSettingsLink,
        manualStoreSetupFinished
    } = store.accountsStore;
    const { getEcommerceRedirect } = store.authStore;
    const { accountHasAllScopes } = store.integrationsStore;
    const { campaigns, hasPreLaunchedCampaign } = store.campaignsStore;
    const notificationsToShow = [];

    const location = useLocation();
    const isNotAccountSettingsPage = !location.pathname.includes(ROUTE_PATHS.INTEGRATIONS_SETTINGS);
    const hasCampaigns = campaigns.length > 0;
    const isNotCampaignPage = !location.pathname.includes(ROUTE_PATHS.CAMPAIGNS);
    const isCampaignPage = location.pathname.includes(ROUTE_PATHS.CAMPAIGNS);

    const isVisible =
        !location.pathname.includes(ROUTE_PATHS.HELP_CENTER) &&
        !location.pathname.includes(ROUTE_PATHS.CAMPAIGNS_CREATE) &&
        !location.pathname.includes(ROUTE_PATHS.ROOT_CAMPAIGNS_EDIT);

    const updateScopes = () => {
        const domains = store.accountsStore?.currentAccount?.domains || [];
        const domain = domains.find((domain) => domain.class === 1)?.shopDomain || '';

        getEcommerceRedirect(PLATFORM_NAME.SHOPIFY, {
            companyName,
            shop: domain,
            token_type: 'offline'
        });
    };

    const platformSettings = settings[currentAccount?.platformId];
    const redirect = (path) => {
        const baseLink = isShopify ? shopifySettingsLink : bigcommerceSettingsLink;
        const url = path.includes('https') ? '' : baseLink;
        window.open(url + path, '_blank');
    };

    const notificationsMap = createNotificationsMap(
        {
            [NOTIFICATION_UPDATE]: updateScopes,
            [CURRENCY]: () => redirect(platformSettings.currency),
            [BAD_PLAN]: () => redirect(platformSettings.badPlan),
            [MISSING_US_SHIPPING_METHOD]: () => redirect(platformSettings.missedShipping),
            [INTEGRATION_INACTIVE]: () => redirect(platformSettings.integrationInactive),
            [SETUP_REQUIRED]: () => redirect(platformSettings.setupRequired)
        },
        me?.permission?.role
    );

    if (
        !manualStoreSetupFinished &&
        isManual &&
        isNotAccountSettingsPage &&
        (isNotCampaignPage || hasCampaigns) &&
        !(isCampaignPage && hasPreLaunchedCampaign)
    ) {
        notificationsToShow.push(notificationsMap[MANUAL_STORE_ONBOARDING]);
    }

    if (!isManual && setupInfo.purchaseEventStatus !== PURCHASE_EVENT_STATUS.CONNECTED && isNotAccountSettingsPage) {
        notificationsToShow.push(notificationsMap[SCRIPT_TAG_THANK_YOU_PAGE_CONNECT]);
    }

    if (!isManual && setupInfo.scriptTag.status !== SCRIPT_TAG_STATUS.CONNECTED && isNotAccountSettingsPage) {
        notificationsToShow.push(notificationsMap[SCRIPT_TAG_CONNECT]);
    }

    if (isPaused) {
        notificationsToShow.push(notificationsMap[ACCOUNT_ON_PAUSE]);
    } else if (isUninstalled && !isManual) {
        notificationsToShow.push(notificationsMap[ACCOUNT_UNINSTALLED]);
    }

    if (showPaymentFailedModal) {
        notificationsToShow.push(notificationsMap[PAYMENT_CONNECT]);
    }

    if (!accountHasAllScopes && !isUninstalled && isShopify && isNotAccountSettingsPage) {
        notificationsToShow.push(notificationsMap[NOTIFICATION_UPDATE]);
    }

    if (isBlocked) {
        const firstReason = notificationsMap[currentAccount.statusBlockReasons[0]];

        notificationsToShow.length = 0;
        notificationsToShow.push(firstReason);
    }

    if (!notificationsToShow.length) {
        return null;
    }

    return isVisible ? (
        <StyledNotificationsWrapper>
            <NotificationsList notifications={notificationsToShow} />
        </StyledNotificationsWrapper>
    ) : null;
});

export default TopNotificationsSection;
